// src/lib/scores/guarantee-copy.ts
//
// The words every guarantee surface uses for HOW the baseline is set, so the
// /guarantee page, the status card and /help say the same thing — and say what
// the code does (guarantee-baseline.ts, guarantee.ts, effective-plan.ts).
// Tests in __tests__/guarantee-copy.test.ts pin these sentences to the code
// constants they describe; change the rule and the copy together.
//
// External QA (2026-10): the status card said "Take a Checkpoint", but separate
// Math-only and Reading & Writing-only Checkpoints never set a baseline (only a
// two-section one does), the card did not say where an existing baseline came
// from, and nothing explained that a later full-length SAT replaces a
// Checkpoint baseline or when a school student is covered.

import { GUARANTEE_MIN_PRACTICE_QUESTIONS, GUARANTEE_PROMISED_GAIN } from './guarantee'
import type { GuaranteeBaselineSource } from './guarantee-baseline'

/** Where a student takes the assessment that sets a baseline. */
export const GUARANTEE_CHECKPOINT_HREF = '/checkpoint'
/** A full-length practice SAT also sets it (and outranks a Checkpoint). */
export const GUARANTEE_FULL_SAT_HREF = '/sat'

/** What counts as a baseline (guarantee-baseline.ts, in plain words). */
export const GUARANTEE_BASELINE_RULE =
  'Your baseline is your first full-length practice SAT on UnlimitedTests if you have completed ' +
  'one. Otherwise it is your first two-section Checkpoint: Reading and Writing and Math in the ' +
  'same Checkpoint, with at least a quarter of its questions answered. A Math-only or a Reading ' +
  'and Writing-only Checkpoint never sets a baseline, even if you take one of each.'

/** What a later full SAT does to a Checkpoint baseline (the SAT is checked first). */
export const GUARANTEE_LATER_FULL_SAT_RULE =
  'If your baseline came from a Checkpoint and you later complete a full-length practice SAT, ' +
  'your baseline becomes that SAT’s score, because your first full-length SAT always comes first, ' +
  'whenever you took it. After that, more tests don’t change it.'

/** The first two-section Checkpoint had too few answers; later ones aren't used instead. */
export const GUARANTEE_SPARSE_CHECKPOINT_RULE =
  'Your first two-section Checkpoint had fewer than a quarter of its questions answered, so it ' +
  'can’t set a baseline, and later Checkpoints aren’t used in its place. Complete a full-length ' +
  'practice SAT to set one.'

/**
 * Who is covered, and when a school student's coverage and free month begin.
 * Coverage = getEffectivePlan(user) === 'PRO' at the moment the status is
 * computed or a claim is reviewed (/api/guarantee, getClaimSnapshotForUser) —
 * a school license counts while it is active. The free month is applied by
 * staff as Comp access after a claim is granted, from the day it is applied.
 */
export const GUARANTEE_COVERAGE_RULE =
  'You’re covered while you have Pro: a subscription, Pro through your school, or Pro access ' +
  'from our team. We check this when your status is shown and again when a claim is reviewed, ' +
  'not when you took your tests.'

export const GUARANTEE_SCHOOL_RULE =
  'Pro through your school counts while your school’s license is active, from its start date to ' +
  'its end date. If your school’s license has ended by the time you claim, the shortfall can’t be ' +
  'claimed unless you have Pro another way. If a claim is approved, our team adds the free month ' +
  'as Pro access starting the day it’s applied; if your school’s license is still active then, ' +
  'that month runs alongside it.'

/** The promise in one line, from the constants. */
export const GUARANTEE_PROMISE =
  `If you’re a Pro member who has answered at least ` +
  `${GUARANTEE_MIN_PRACTICE_QUESTIONS.toLocaleString('en-US')} practice questions and your ` +
  `verified official College Board score isn’t at least ${GUARANTEE_PROMISED_GAIN} points above ` +
  `your baseline, we’ll give you a month of Pro free.`

/** "your full-length practice SAT" / "your two-section Checkpoint". */
export function baselineSourceLabel(source: GuaranteeBaselineSource): string {
  return source === 'full-sat' ? 'your full-length practice SAT' : 'your two-section Checkpoint'
}

/** Results page for the attempt that set the baseline. */
export function baselineResultsHref(source: GuaranteeBaselineSource, attemptId: string): string {
  return source === 'full-sat'
    ? `/attempt/${attemptId}/results`
    : `/checkpoint/results/${attemptId}`
}

/** "September 3, 2026" in the viewer's zone (client) or UTC when pinned. */
export function formatBaselineDate(iso: string, timeZone?: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  })
}
